import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
    getUnreadCountApi,
    listNotificationsApi,
    markAllNotificationsReadApi,
    markNotificationReadApi,
} from '../../../modules/Notifications/query';
import { resolveNotificationDestination } from '../../../modules/Notifications/helpers';
import NotificationItem from '../../../modules/Notifications/NotificationItem';

const POLL_INTERVAL_MS = 60_000;

/**
 * Header bell: unread badge + dropdown preview of the current user's in-app notifications.
 *
 * The first unread-count fetch is deferred to the window `load` event rather than firing in
 * componentDidMount, so it never competes with the page's own critical-path requests. The
 * dropdown itself only ever shows the first page (whatever the API's default page size is) --
 * it is a preview, not a list; "View all" is what takes the user to the full, paginated
 * /notifications/ page.
 */
class NotificationsBell extends Component {
    constructor(props) {
        super(props);

        this.rootRef = createRef();
        this.onToggle = this.onToggle.bind(this);
        this.onDocumentMouseDown = this.onDocumentMouseDown.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.onWindowLoad = this.onWindowLoad.bind(this);
        this.fetchUnreadCount = this.fetchUnreadCount.bind(this);
        this.fetchFirstPage = this.fetchFirstPage.bind(this);
        this.onMarkOneRead = this.onMarkOneRead.bind(this);
        this.onMarkAllRead = this.onMarkAllRead.bind(this);
        this.onItemClick = this.onItemClick.bind(this);

        this.pollTimer = null;

        this.state = {
            isOpen: false,
            unreadCount: 0,
            etag: null,
            notifications: [],
            isLoadingList: false,
            hasLoadedOnce: false,
        };
    }

    componentDidMount() {
        if (document.readyState === 'complete') {
            this.onWindowLoad();
        } else {
            window.addEventListener('load', this.onWindowLoad);
        }
        document.addEventListener('mousedown', this.onDocumentMouseDown);
        document.addEventListener('keydown', this.onDocumentKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('load', this.onWindowLoad);
        document.removeEventListener('mousedown', this.onDocumentMouseDown);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
        if (this.pollTimer) clearInterval(this.pollTimer);
    }

    onWindowLoad() {
        window.removeEventListener('load', this.onWindowLoad);
        this.fetchUnreadCount();
        this.pollTimer = setInterval(this.fetchUnreadCount, POLL_INTERVAL_MS);
    }

    onDocumentMouseDown(e) {
        if (this.state.isOpen && !this.rootRef.current?.contains(e.target)) {
            this.setState({ isOpen: false });
        }
    }

    onDocumentKeyDown(e) {
        if (e.key === 'Escape') {
            this.setState({ isOpen: false });
        }
    }

    async fetchUnreadCount() {
        try {
            const res = await getUnreadCountApi(this.state.etag);
            if (res.status === 304) return;
            if (!res.ok) return;
            const data = await res.json();
            this.setState({ unreadCount: data.unread, etag: res.headers.get('ETag') });
        } catch (err) {
            console.log(err);
        }
    }

    async fetchFirstPage() {
        this.setState({ isLoadingList: true });
        try {
            const res = await listNotificationsApi();
            if (!res.ok) return;
            const data = await res.json();
            this.setState({ notifications: data.results, hasLoadedOnce: true });
        } catch (err) {
            console.log(err);
        } finally {
            this.setState({ isLoadingList: false });
        }
    }

    onToggle() {
        this.setState((prevState) => {
            const willOpen = !prevState.isOpen;
            if (willOpen && !prevState.hasLoadedOnce) this.fetchFirstPage();
            return { isOpen: willOpen };
        });
    }

    markLocalNotificationRead(notificationId) {
        this.setState((prevState) => ({
            notifications: prevState.notifications.map((n) =>
                n.id === notificationId && !n.read_at
                    ? { ...n, read_at: new Date().toISOString() }
                    : n,
            ),
            unreadCount: Math.max(0, prevState.unreadCount - 1),
        }));
    }

    async onMarkOneRead(notification) {
        if (notification.read_at) return;
        this.markLocalNotificationRead(notification.id);
        try {
            await markNotificationReadApi(notification.id);
        } catch (err) {
            console.log(err);
        }
    }

    async onMarkAllRead() {
        this.setState((prevState) => ({
            notifications: prevState.notifications.map((n) =>
                n.read_at ? n : { ...n, read_at: new Date().toISOString() },
            ),
            unreadCount: 0,
        }));
        try {
            await markAllNotificationsReadApi();
        } catch (err) {
            console.log(err);
        }
    }

    onItemClick(notification) {
        if (!notification.read_at) this.markLocalNotificationRead(notification.id);

        const destination = resolveNotificationDestination(notification);
        if (destination) window.location.href = destination;
    }

    render() {
        const { t } = this.props;
        const { isOpen, unreadCount, notifications, isLoadingList, hasLoadedOnce } = this.state;

        return (
            <li className="list-group notifications-bell" ref={this.rootRef}>
                <button
                    type="button"
                    className="btn-icon"
                    aria-label={t('layout.notifications.title')}
                    aria-expanded={isOpen}
                    onClick={this.onToggle}
                >
                    <i className="icon-fi_bell nav-icon"></i>
                    {unreadCount > 0 && (
                        <span className="notifications-badge">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>
                {isOpen && (
                    <div className="notifications-panel">
                        <div className="notifications-panel-header">
                            <span>{t('layout.notifications.title')}</span>
                            {unreadCount > 0 && (
                                <button type="button" onClick={this.onMarkAllRead}>
                                    {t('layout.notifications.mark_all_read')}
                                </button>
                            )}
                        </div>
                        <div className="notifications-panel-list">
                            {isLoadingList && !hasLoadedOnce && (
                                <div className="notifications-panel-loading">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">
                                            {t('layout.notifications.loading')}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {!isLoadingList && hasLoadedOnce && notifications.length === 0 && (
                                <div className="notifications-panel-empty">
                                    {t('layout.notifications.empty')}
                                </div>
                            )}
                            {notifications.map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onClick={this.onItemClick}
                                    onMarkRead={this.onMarkOneRead}
                                />
                            ))}
                        </div>
                        <a href="/notifications/" className="notifications-panel-viewall">
                            {t('layout.notifications.view_all')}
                        </a>
                    </div>
                )}
            </li>
        );
    }
}

export default withTranslation()(NotificationsBell);
